import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CountUp } from "@/components/CountUp";
import { api, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  TrendingUp,
  ShoppingCart,
  Users,
  Receipt,
  ArrowUpRight,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AgentDashboard } from "@/components/AgentDashboard";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { t } from "@/lib/i18n";

const fade = (i) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.05, duration: 0.35 },
});

function KpiCard({ icon: Icon, label, value, accent, i }) {
  return (
    <motion.div
      {...fade(i)}
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="bg-card border border-border rounded-2xl p-5 hover:border-primary/40 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow,border-color] duration-200"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono-j uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className={`h-9 w-9 rounded-xl grid place-items-center ${accent}`}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
      </div>
      <p className="font-display text-3xl font-extrabold mt-4">{value}</p>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return user?.role === "agent" ? <AgentDashboard /> : <CompanyDashboard />;
}

function CompanyDashboard() {
  const { user, activeCompanyId } = useAuth();
  const navigate = useNavigate();
  const isCompany = user?.role === "company";
  const cid = isCompany ? user?.company_id : activeCompanyId;

  const { data: stats } = useQuery({
    queryKey: ["stats", "all", cid],
    queryFn: async () => (await api.get("/stats", { params: { company_id: cid } })).data,
    enabled: !!cid,
  });
  const { data: orders = [] } = useQuery({
    queryKey: ["orders", cid],
    queryFn: async () => (await api.get("/orders", { params: { company_id: cid } })).data,
    enabled: !!cid,
  });

  const recent = orders.slice(0, 6);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono-j uppercase tracking-wider text-muted-foreground">
            {t("Benvenuto,")} {user?.name?.split(" ")[0]}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
            {t("Panoramica")}
          </h1>
        </div>
        <Button onClick={() => navigate("/orders/new")} className="gap-2 font-semibold" data-testid="dash-new-order-btn">
          <Plus className="h-4 w-4" /> {t("Nuovo ordine")}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard i={0} icon={TrendingUp} label={t("Vendite totali")} value={<CountUp value={stats?.total_sales} format={eur} />} accent="bg-primary/20 text-primary" />
        <KpiCard i={1} icon={ShoppingCart} label={t("Ordini")} value={<CountUp value={stats?.orders_count} />} accent="bg-secondary/20 text-secondary" />
        <KpiCard i={2} icon={Receipt} label={t("Ordine medio")} value={<CountUp value={stats?.avg_order} format={eur} />} accent="bg-accent/20 text-accent" />
        <KpiCard i={3} icon={Users} label={isCompany ? t("Agenti attivi") : t("Miei ordini")} value={<CountUp value={isCompany ? stats?.active_agents : stats?.orders_count} />} accent="bg-emerald-500/20 text-emerald-400" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <motion.div {...fade(4)} className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
          <h3 className="font-semibold mb-4">{t("Andamento fatturato")}</h3>
          <div className="h-[280px]">
            {stats?.revenue_trend?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.revenue_trend}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#4F46E5" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#64748b" fontSize={11} width={48} />
                  <Tooltip
                    contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 12, color: "#fff" }}
                    formatter={(v) => [eur(v), "Fatturato"]}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#4F46E5" strokeWidth={2.5} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-muted-foreground text-sm">
                {t("Nessun dato ancora. Crea il primo ordine.")}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div {...fade(5)} className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">{t("Ordini recenti")}</h3>
            <button onClick={() => navigate("/orders")} className="text-xs text-primary flex items-center gap-1 hover:underline">
              {t("Tutti")} <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
          <div className="space-y-3">
            {recent.length ? (
              recent.map((o) => (
                <div key={o.order_id} className="flex items-center justify-between border-b border-border/60 pb-3 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{o.customer?.name}</p>
                    <p className="text-xs text-muted-foreground font-mono-j">{o.order_no}</p>
                  </div>
                  <span className="text-sm font-semibold text-primary whitespace-nowrap">{eur(o.grand_total)}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">{t("Nessun ordine.")}</p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
